export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number
  ) {
    super(message);
  }
}

export async function apiJson<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = await getShopifyIdToken();
  return fetchJson<T>(path, init, token);
}

export async function bootstrapShopifySession(): Promise<boolean> {
  const token = await getShopifyIdToken(3000);
  if (!token) return false;

  await fetchJson("/api/bootstrap", { method: "POST" }, token);
  return true;
}

export function isShopifyEmbeddedApp(): boolean {
  return Boolean(window.shopify || new URLSearchParams(window.location.search).get("host"));
}

async function fetchJson<T>(path: string, init: RequestInit = {}, token: string | null = null): Promise<T> {
  const headers = new Headers(init.headers);
  headers.set("Accept", "application/json");

  if (init.body && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }

  if (token) headers.set("Authorization", `Bearer ${token}`);

  const response = await fetch(path, { ...init, headers });
  const isJson = response.headers.get("Content-Type")?.includes("application/json");
  const payload = isJson ? await response.json() : await response.text();

  if (!response.ok) {
    const errorPayload = payload as { error?: string };
    const message = typeof payload === "string" ? payload : errorPayload.error ?? "Request failed";
    throw new ApiError(message, response.status);
  }

  return payload as T;
}

async function getShopifyIdToken(waitMs = 0): Promise<string | null> {
  const getter = await waitForShopifyIdToken(waitMs);
  if (!getter) return null;
  return getter();
}

async function waitForShopifyIdToken(waitMs: number): Promise<(() => Promise<string>) | null> {
  const startedAt = Date.now();

  while (Date.now() - startedAt <= waitMs) {
    const getter = window.shopify?.idToken;
    if (getter) return getter.bind(window.shopify);
    if (waitMs === 0) break;
    await new Promise((resolve) => window.setTimeout(resolve, 50));
  }

  return null;
}

export async function downloadFile(path: string): Promise<void> {
  const token = await getShopifyIdToken(3000);
  const headers = new Headers({ Accept: "text/csv" });
  if (token) headers.set("Authorization", `Bearer ${token}`);

  const response = await fetch(path, { headers });
  if (!response.ok) {
    const message = response.headers.get("Content-Type")?.includes("application/json")
      ? ((await response.json()) as { error?: string }).error ?? "Download failed"
      : await response.text();
    throw new ApiError(message, response.status);
  }

  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = objectUrl;
  link.download = filenameFromDisposition(response.headers.get("Content-Disposition")) ?? "gmc-feed-preflight-export.csv";
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(objectUrl);
}

function filenameFromDisposition(value: string | null): string | null {
  if (!value) return null;
  const match = value.match(/filename="([^"]+)"/i) ?? value.match(/filename=([^;]+)/i);
  return match?.[1]?.trim() ?? null;
}
